import { $ } from "../lib/format";
import { ACCENT2, AMBER, BG, DARK, DIM, MID, RED } from "../theme";
import { compareMargin, jobCompleteness, marginNeedsMoreData, marginView } from "../jobCompleteness";
import { useBusinessType } from "../useBusinessType";
import { useState } from "react";

// ─── TAB: CLIENT SCORECARD ────────────────────────────────────────────────────

// Words on the chip next to a client. "Incomplete" only when the client's margin is withheld; otherwise the chip
// says what is wrong: expenses to review if that is the only gap, else how many jobs are missing numbers.
function clientChipText(cl) {
  if (cl.marginView.kind === "incomplete") return cl.incompleteJobs === cl.jobs ? "Incomplete" : `${cl.incompleteJobs} of ${cl.jobs} jobs incomplete`;
  if (cl.otherGaps === 0 && (cl.pendingExpenses > 0 || cl.noCostsJobs > 0)) {
    const parts = [];
    if (cl.pendingExpenses > 0) parts.push(cl.pendingExpenses === 1 ? "1 expense to review" : `${cl.pendingExpenses} expenses to review`);
    if (cl.noCostsJobs > 0) parts.push(cl.noCostsJobs === 1 ? "1 job with no costs recorded" : `${cl.noCostsJobs} jobs with no costs recorded`);
    return parts.join(", ");
  }
  return `${cl.incompleteJobs} of ${cl.jobs} jobs missing numbers`;
}

function ClientScorecard({ jobSummaries, pendingItems }) {
  const [sort, setSort] = useState("profit");
  const { rule } = useBusinessType();

  // Aggregate jobs by client
  const clientMap = {};
  jobSummaries.forEach(j => {
    if (!clientMap[j.clientName]) {
      clientMap[j.clientName] = { name: j.clientName, jobs: 0, revenue: 0, costs: 0, profit: 0, outstanding: 0, incompleteJobs: 0, pendingExpenses: 0, noCostsJobs: 0, otherGaps: 0, marginMissing: false };
    }
    // A client's margin is only as complete as its jobs: if any job is missing costs or labor, say so.
    const jc = jobCompleteness(j, pendingItems, rule);
    if (jc.status === "incomplete") clientMap[j.clientName].incompleteJobs++;
    clientMap[j.clientName].pendingExpenses += jc.pendingCount;
    if (jc.flags.noCosts && !jc.flags.noRevenue) clientMap[j.clientName].noCostsJobs++;
    // a gap other than expenses to review or no costs (those two have their own wording on the chip)
    if (jc.reasons.filter(r => r !== "No costs recorded").length > (jc.pendingCount > 0 ? 1 : 0)) clientMap[j.clientName].otherGaps++;
    if (marginNeedsMoreData(jc.flags)) clientMap[j.clientName].marginMissing = true;
    clientMap[j.clientName].jobs++;
    clientMap[j.clientName].revenue    += j.revenue;
    clientMap[j.clientName].costs      += j.costs;
    clientMap[j.clientName].profit     += j.profit;
    clientMap[j.clientName].outstanding += j.outstanding;
  });

  const clients = Object.values(clientMap).map(c => {
    // Same margin rule as a job: no revenue -> a dash; positive profit with costs or labor missing -> Incomplete.
    const cc = { flags: { noCosts: c.marginMissing, noLabor: false } };
    return {
      ...c,
      marginView: marginView({ revenue: c.revenue, costs: c.costs }, cc),
      avgJobSize: c.jobs > 0 ? Math.round(c.revenue / c.jobs) : 0,
    };
  }).sort((a, b) => {
    if (sort === "profit")   return b.profit - a.profit;
    if (sort === "revenue")  return b.revenue - a.revenue;
    if (sort === "margin")   return compareMargin(a.marginView, b.marginView, "desc");
    if (sort === "jobs")     return b.jobs - a.jobs;
    return a.name.localeCompare(b.name);
  });

  const topClient = clients[0];

  return (
    <div className="r-page" style={{ padding:"32px 36px", background:BG, minHeight:"100vh" }}>
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontFamily:"'Lora',serif",fontSize:24,fontWeight:600,color:DARK,letterSpacing:"-0.02em" }}>Client Profitability</h1>
        <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:DIM,marginTop:4 }}>Which clients consistently bring your most profitable work?</p>
      </div>

      {/* Top client highlight */}
      {topClient && (
        <div className="r-wrap r-gap-wide" style={{ marginBottom:24,padding:"20px 24px",borderRadius:6,border:`1px solid rgba(92,122,90,0.3)`,background:"rgba(92,122,90,0.04)",display:"flex",alignItems:"center",gap:32 }}>
          <div>
            <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:10,letterSpacing:"0.1em",color:DIM,textTransform:"uppercase",marginBottom:6,fontWeight:500 }}>Top Client by Profit</div>
            <div style={{ fontFamily:"'Lora',serif",fontSize:20,fontWeight:500,color:DARK }}>{topClient.name}</div>
          </div>
          {[
            { label:"Total Profit",    val:$(topClient.profit) },
            { label:"Total Revenue",   val:$(topClient.revenue) },
            { label:"Avg Margin",      val:topClient.marginView.text },
            { label:"Jobs",            val:topClient.jobs },
            { label:"Avg Job Size",    val:$(topClient.avgJobSize) },
          ].map((k,i) => (
            <div key={i}>
              <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:9,letterSpacing:"0.1em",color:DIM,textTransform:"uppercase",marginBottom:4,fontWeight:500 }}>{k.label}</div>
              <div style={{ fontFamily:"'Lora',serif",fontSize:18,fontWeight:500,color:ACCENT2 }}>{k.val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Sort controls */}
      <div className="r-wrap" style={{ display:"flex",alignItems:"center",gap:8,marginBottom:16 }}>
        <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:DIM }}>Sort by:</span>
        {[["profit","Total Profit"],["revenue","Revenue"],["margin","Avg Margin"],["jobs","# Jobs"],["name","Name"]].map(([k,l]) => (
          <button key={k} className={`btn${sort===k?" act":""}`} onClick={()=>setSort(k)}>{l}</button>
        ))}
      </div>

      {/* Client table */}
      <div className="card" style={{ overflow:"hidden" }}>
        <div className="thead r-hide-phone" style={{ gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 1fr" }}>
          {["Client","# Jobs","Total Revenue","Total Costs","Total Profit","Avg Margin"].map(h => (
            <div key={h} className="th">{h}</div>
          ))}
        </div>
        {clients.map((cl, i) => {
          const win = cl.profit > 0;
          return (
            <div key={i} className="trow r-client-row" style={{ gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 1fr",borderLeft:`3px solid ${win?ACCENT2:RED}` }}>
              <div className="tcell" style={{ color:DARK,fontWeight:500,fontFamily:"'DM Sans',sans-serif" }}>
                {cl.name}
                {(cl.incompleteJobs > 0 || cl.noCostsJobs > 0) && <span className="chip a" title="Some of this client's jobs are missing revenue, costs, labor or expenses still to assign" style={{ marginLeft:8,fontSize:9,padding:"1px 7px" }}>{clientChipText(cl)}</span>}
                {cl.outstanding > 0 && <span style={{ marginLeft:8,fontSize:9,padding:"2px 7px",borderRadius:3,background:"rgba(140,107,48,0.1)",color:AMBER,fontWeight:500 }}>A/R {$(cl.outstanding)}</span>}
              </div>
              <div className="tcell mono" data-label="Jobs" style={{ color:MID,fontSize:12 }}>{cl.jobs}</div>
              <div className="tcell mono" data-label="Revenue" style={{ color:MID,fontSize:12 }}>{$(cl.revenue)}</div>
              <div className="tcell mono" data-label="Costs" style={{ color:MID,fontSize:12 }}>{$(cl.costs)}</div>
              <div className="tcell" data-label="Profit"><span className={`chip ${win?"g":"r"}`}>{win?"+":""}{$(cl.profit)}</span></div>
              <div className="tcell" data-label="Margin"><span className={`chip ${cl.marginView.kind !== "number" ? "a" : cl.marginView.value>=20?"g":cl.marginView.value>=0?"a":"r"}`}>{cl.marginView.text}</span></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { ClientScorecard };
